import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {RecentlyPlayedGames} from "./games.service.interface";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {Check} from "@sparkware/app-utils";
import {LocalStorageKeys} from "../storage/local-storage-keys";

export class RecentlyPlayedGamesReader {
    constructor(private readonly services: IServiceFactoryExtended) {
        this._migrateOldRecentlyPlayedGamesStorage();
        this._loadFromLocalStorage();
        makeObservable(this);
        reaction(() => this.services.user.isAuthorized,
            () => {
                this._waitForRecentlyPlayedPromise = this._loadFromServer();
            }, {
                fireImmediately: true
            });

        this.services.gameRunner.then((gameRunner) => {
            reaction(() => gameRunner.currentRunningGame,
                (currentRunningGame) => {
                    if (currentRunningGame) {
                        this._addGameToRecentlyPlayed(currentRunningGame.gameInfo.gameType);
                    }
                })
        })
    }

    @observable.ref
    private _recentlyPlayedGames: RecentlyPlayedGames = {
        isInitialized: false,
        gameTypes: []
    }

    get recentlyPlayedGames(): RecentlyPlayedGames {
        return this._recentlyPlayedGames;
    }

    private _waitForRecentlyPlayedPromise: Promise<void> = Promise.resolve();
    async getRecentlyPlayedGamesAsync(): Promise<number[]> {
        await this._waitForRecentlyPlayedPromise;
        return this.recentlyPlayedGames.gameTypes;
    }

    private setRecentlyPlayedGames(gameTypes: number[]) {
        runInAction(() => {
            this._recentlyPlayedGames = {
                isInitialized: true,
                gameTypes: gameTypes
            }
        });

        this.services.localStorage.setJson(LocalStorageKeys.recentlyPlayedGames, gameTypes);
        this.services.sdk.eventsTrigger.recentlyPlayedGamesUpdated(gameTypes);
    }


    private _loadFromLocalStorage(): void {
        const gameTypes = this.services.localStorage.getJson<number[]>(LocalStorageKeys.recentlyPlayedGames);
        if(!gameTypes) {
            return;
        }

        this.setRecentlyPlayedGames(gameTypes);
    }

    private async _loadFromServer(): Promise<void> {
        if(!this.services.user.isAuthorized) {
            return;
        }

        try {
            const response = await this.services.api.historyController.getRecentlyPlayedGames();
            this.setRecentlyPlayedGames(response.gameTypes);
        } catch (err) {
            this.services.logger.error('Failed to read recently played from server', err);
        }

    }

    private _addGameToRecentlyPlayed(gameType: number): void {
        const gameTypes = [...this.recentlyPlayedGames.gameTypes];
        const existingIndex = gameTypes.findIndex(gt => gameType === gt);
        if(existingIndex >= 0) {
            gameTypes.splice(existingIndex, 1);
        }

        this.setRecentlyPlayedGames([gameType, ...gameTypes]);
    }

    //TODO - this method should be removed let's say 1 year after this code reach production (which probably will happen in April 2025)
    private _migrateOldRecentlyPlayedGamesStorage(): void {
        const oldStorageData = this.services.localStorage.getItem('cgp_recently_played_games');
        if(!oldStorageData) {
            return;
        }

        const gameTypes = oldStorageData.split(",")
                                                 .map(gameType => parseInt(gameType))
                                                 .filter(gameType => Check.isNumber(gameType));
        this.services.localStorage.setJson(LocalStorageKeys.recentlyPlayedGames, gameTypes);
        this.services.localStorage.removeItem('cgp_recently_played_games')
    }
}